import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { loadRebootHistory } from '@/redux/actions/productDetail';
import { vpsOciActions } from '@/redux/modules/productDetail/vpsOci';

import * as Styles from './VpsOciDetailPage.styles';

import useLocale from '@/hooks/useLocale';
import { useIsMobile } from '@/hooks/useIsMobile';

import { differenceInMilliseconds, parseISO } from 'date-fns';

import MyPlan from './MyPlan/MyPlan';
import VPSInfo from './VPSInfo/VPSInfo';
import SSHAccess from './SSHAccess/SSHAccess';
import Management from './Management/Management';
import PlanDetails from './PlanDetails/PlanDetails';
import ConfirmationToken from './components/OpenClaw/ConfirmationToken/ConfirmationToken';
import ConfigurationGatorClaw from './components/GatorClaw/ConfigurationGatorClaw';

import { Skeleton } from 'gatorcomponents';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { ENABLE_VPS_OCI_GUIDE_TOUR, ENABLE_VPS_OCI_NEW_SCREEN_UPGRADE } from '@/config/GrowthBook/constants';

import SupportForms from '@/components/SupportForms';
import ChangePlanVpsOciCard from '@/components/Cards/ChangePlanVpsOciCard';
import UpgradePlan from './PlanDetails/components/UpgradePlan/UpgradePlan';
import VpsStoreLoaderPage from './components/VpsStoreLoaderPage/VpsStoreLoaderPage';
import VpsWelcomeModal from './components/VpsWelcomeModal';
import VpsGuidedTour from './components/VpsGuidedTour';
import FaqProductDetails from '@/components/FrequentlyQuestions/FaqProductDetails';
import { VPS_OCI_PAGE_STEPS } from './VpsOciDetailPage.enum';

import ProductTemplatesPage from './ProductTemplatesPage/ProductTemplatesPage';
import { openServerIpUrl } from '@/utils/navigation/redirects';

const TIMER_TO_END_RESTART = 0;
const GATORCLAW_PORT = 47716;

const VpsOciDetailPage = () => {
  const dispatch = useDispatch();

  const isMobile = useIsMobile();
  const [rebootingPercentage, setRebootingPercentage] = useState(0);
  const [formToShow, setFormToShow] = useState(null);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [shouldStartTour, setShouldStartTour] = useState(false);
  const [isReviewingTour, setIsReviewingTour] = useState(false);
  const [showProductTemplatesPage, setShowProductTemplatesPage] = useState(false);
  const [currentStep, setCurrentStep] = useState(VPS_OCI_PAGE_STEPS.DETAILS);

  const { token } = useSelector(state => state.auth);
  const [tourSession, setTourSession] = useState(`${token}-${Date.now()}`);

  const enableVpsOciGuideTour = useFeatureIsOn(ENABLE_VPS_OCI_GUIDE_TOUR);
  const enableVpsOciNewScreenUpgrade = useFeatureIsOn(ENABLE_VPS_OCI_NEW_SCREEN_UPGRADE);

  const myPlanRef = useRef(null);
  const vpsInfoRef = useRef(null);
  const sshAccessRef = useRef(null);
  const managementRef = useRef(null);

  const { id: hostingId } = useParams();


  const vpsOci = useSelector(state => state.vpsOci);
  const { loading: loadingVpsOci, isRebooting } = vpsOci || {};
  const [loading, setLoading] = useState(() => loadingVpsOci || true);
  const { productName, vps, packageId } = (vpsOci && vpsOci.data) || {};

  const productDetail = useSelector(state => state.productDetail.historyBkp) || [];
  const { historyBkp = [] } = productDetail || {};

  const { productDetails: productDetailsLocale } = useLocale();
  const { vpsOci: locale } = productDetailsLocale;
  const isVpsOciOnline = vpsOci.status && vpsOci.status.value === 'online';
  const canShowTour = enableVpsOciGuideTour && vpsOci.tour && vpsOci.tour.canShowTour;
  const canShowVpsOciTour = !loading && canShowTour && isVpsOciOnline;

  const { pendingConfigurations } = vps || {};

  const { serverIp } = (vpsOci && vpsOci.data && vpsOci.data.ssh) || {};
  const hasPendingOpenClawConfiguration = pendingConfigurations && pendingConfigurations.includes('openclaw');
  const hasPendingGatorClawConfiguration = pendingConfigurations && pendingConfigurations.includes('gatorclaw');
  const [showConfigurationOpenClaw, setShowConfigurationOpenClaw] = useState(false);
  const [showConfigurationGatorClaw, setShowConfigurationGatorClaw] = useState(false);

  const gatorClawRedirect = useCallback(() => {
    openServerIpUrl(serverIp, GATORCLAW_PORT);
  }, [serverIp]);

  useEffect(() => {
    setLoading(loadingVpsOci);
  }, [loadingVpsOci]);

  useEffect(() => {
    dispatch(vpsOciActions.data.get({ hostingId }));
  }, [dispatch, hostingId]);

  useEffect(() => {
    if (hasPendingOpenClawConfiguration) {
      setShowConfigurationOpenClaw(true);
    } else {
      setShowConfigurationOpenClaw(false);
    }
  }, [hasPendingOpenClawConfiguration]);

  useEffect(() => {
    if (hasPendingGatorClawConfiguration) {
      setShowConfigurationGatorClaw(true);
    } else {
      setShowConfigurationGatorClaw(false);
    }
  }, [hasPendingGatorClawConfiguration]);

  useEffect(() => {
    dispatch(vpsOciActions.rebuild.reset());
    dispatch(vpsOciActions.changeImage.reset());
    dispatch(vpsOciActions.upgrade.reset());
  }, [dispatch]);

  const timerRef = useRef(null);
  const clearRebootingTimeout = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
  }, []);

  const handleRebootingTimer = useCallback(() => {
    if (historyBkp && historyBkp.length > 0) {
      const leftTimerToRestart = TIMER_TO_END_RESTART - differenceInMilliseconds(new Date(), parseISO(historyBkp[0].datetime));

      const percentage = (leftTimerToRestart / TIMER_TO_END_RESTART) * 100;

      setRebootingPercentage(percentage);

      if (leftTimerToRestart > 0) {
        dispatch(vpsOciActions.reboot.loading(true));
        timerRef.current = setTimeout(() => {
          setRebootingPercentage(100);
          dispatch(vpsOciActions.reboot.loading(false));
        }, leftTimerToRestart);
      } else {
        dispatch(vpsOciActions.reboot.loading(false));
      }
    }
  }, [historyBkp, dispatch]);

  const handleShowForm = useCallback((formName) => {
    setFormToShow(formName);
  }, []);

  const handleStartTour = useCallback(() => {
    setShowWelcomeModal(false);
    setTimeout(() => {
      setShouldStartTour(true);
    }, 100);
  }, []);

  const handleReviewTour = useCallback(() => {
    setTourSession(`${token}-${Date.now()}`);
    setShouldStartTour(true);
    setIsReviewingTour(true);
  }, [token]);

  const handleTourClose = useCallback(() => {
    setShouldStartTour(false);
    setTourSession(null);
  }, []);

  const saveTourDismissed = useCallback(() => {
    !isReviewingTour && dispatch(vpsOciActions.tour.saveStep({ step: 'tour_dismissed', hostingId, session: tourSession }));
    setShowWelcomeModal(false);
    setTourSession(null);
  }, [dispatch, isReviewingTour, hostingId, tourSession]);

  useEffect(() => {
    handleRebootingTimer();
    return () => clearRebootingTimeout();
  }, [historyBkp, dispatch, clearRebootingTimeout, handleRebootingTimer]);

  useEffect(() => {
    dispatch(vpsOciActions.tour.get({ hostingId }));
  }, [dispatch, hostingId]);

  useEffect(() => {
    if (canShowVpsOciTour) {
      !tourSession && setTourSession(`${token}-${Date.now()}`);
      setShowWelcomeModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canShowVpsOciTour, token]);

  const handleLoadRebootHistory = useCallback(() => {
    if (hostingId) {
      dispatch(loadRebootHistory(hostingId));
    }
  }, [hostingId, dispatch]);

  const handleNavigateToStep = useCallback((step) => {
    if (enableVpsOciNewScreenUpgrade) {
      setCurrentStep(step);
    } else {
      handleShowForm('upgradePlan');
    }
  }, []);

  useEffect(() => {
    handleLoadRebootHistory();
  }, [handleLoadRebootHistory]);

  if (loading) {
    return (
      <Styles.Container data-testid="vpsoci-detail-page-loading">
        <Styles.Row>
          <Skeleton width="100%" height="116px" />
        </Styles.Row>
        <Styles.Row>
          <Skeleton width="100%" height="470px" />
          <Skeleton width="100%" height="470px" />
        </Styles.Row>
        <Styles.Row>
          <Skeleton width="100%" height="178px" />
        </Styles.Row>
        <Styles.Row>
          <Skeleton width="100%" height="144px" />
        </Styles.Row>
        <Styles.Row>
          <Skeleton width="100%" height="116px" />
          <Skeleton width="100%" height="116px" />
        </Styles.Row>
      </Styles.Container>
    );
  }

  if (!vpsOci || !vpsOci.data || Object.keys(vpsOci).length === 0 || Object.keys(vpsOci.data).length === 0) return null;

  if (productName) {
    const getProductNamePackageVPSOci = (productName && vps.domain) ? `${productName} - ${vps.domain}` : '';

    if (formToShow === 'upgradePlan') {
      return (
        <SupportForms
          serviceType={{
            title: locale.planDetails.form.title,
            onSuccess: () => setFormToShow(''),
            onBackForm: () => setFormToShow(''),
            lockedOptions: getProductNamePackageVPSOci !== '',
            defaultValues: {
              defaultSelectedProductOption: getProductNamePackageVPSOci,
              defaultSelectedSector: 'billing',
              defaultSelectedNeed: locale.planDetails.form.defaultSelectedNeed,
            },
          }}
        />
      );
    }

    if (formToShow === 'changePlan') {
      return (
        <SupportForms
          serviceType={{
            title: locale.changePlan.form.title,
            onSuccess: () => setFormToShow(''),
            onBackForm: () => setFormToShow(''),
            lockedOptions: true,
            defaultValues: {
              defaultSelectedProductOption: getProductNamePackageVPSOci,
              defaultSelectedSector: 'billing',
              defaultSelectedNeed: locale.changePlan.form.defaultSelectedNeed,
            },
          }}
        />
      );
    }
  }

  if (isRebooting) {
    return (
      <VpsStoreLoaderPage
        progress={rebootingPercentage}
        title={locale.myPlan.feedback.loading}
      />
    );
  }

  if (showConfigurationGatorClaw) {
    return (
      <Styles.Container>
        <ConfigurationGatorClaw
          onShowConfigurationGatorClaw={setShowConfigurationGatorClaw}
          onGatorClawRedirect={gatorClawRedirect}
        />
      </Styles.Container>
    );
  }

  if (showConfigurationOpenClaw) {
    return (
      <Styles.Container>
        <ConfirmationToken
          onShowConfigurationOpenClaw={setShowConfigurationOpenClaw}
        />
      </Styles.Container>
    );
  }

  if (showProductTemplatesPage) {
    return (
      <ProductTemplatesPage
        setShowProductTemplatesPage={setShowProductTemplatesPage}
      />
    );
  }

  if (currentStep === VPS_OCI_PAGE_STEPS.UPGRADE_LISTING || currentStep === VPS_OCI_PAGE_STEPS.UPGRADE_DETAILS) {
    return (
      <Styles.Container data-testid="vpsoci-detail-upgrade-page">
        <UpgradePlan
          currentStep={currentStep}
          onNavigateToStep={handleNavigateToStep}
        />
      </Styles.Container>
    );
  }

  return (
    <Styles.Container data-testid="vpsoci-detail-page">

      <Styles.Row>
        <MyPlan ref={myPlanRef} />
      </Styles.Row>

      <Styles.Row>
        <Styles.Collumn>

          <VPSInfo ref={vpsInfoRef} />

          {isMobile && <SSHAccess ref={sshAccessRef} />}

          <Management
            ref={managementRef}
            setShowProductTemplatesPage={setShowProductTemplatesPage}
            setShowConfigurationGatorClaw={setShowConfigurationGatorClaw}
            hasPendingGatorClawConfiguration={hasPendingGatorClawConfiguration}
          />

          <PlanDetails onNavigateToStep={handleNavigateToStep} />

          <ChangePlanVpsOciCard
            locales={{
              title: locale.changePlan.hireBanner.title,
              description: locale.changePlan.hireBanner.description,
              buttonLabel: locale.changePlan.hireBanner.button,
            }}
            onGoToForm={() => handleShowForm('changePlan')}
          />

        </Styles.Collumn>

        <Styles.Collumn $width={isMobile ? '100%' : '400px'}>
          {!isMobile && <SSHAccess ref={sshAccessRef} />}
          {packageId && (
            <FaqProductDetails
              packageid={packageId}
              onSecondaryAction={enableVpsOciGuideTour && handleReviewTour}
              secondaryButtonLabel={locale.helpLinks.tourButton}
              showSameWithoutQuestions
              testId="vpsoci-detail-page-faq-product-details"
            />
          )}
        </Styles.Collumn>
      </Styles.Row>

      {showWelcomeModal && (
        <VpsWelcomeModal
          onClose={saveTourDismissed}
          onStartTour={handleStartTour}
          tourSession={tourSession}
        />
      )}

      <VpsGuidedTour
        myPlanRef={myPlanRef.current}
        vpsInfoRef={vpsInfoRef.current}
        sshAccessRef={sshAccessRef.current}
        managementRef={managementRef.current}
        onClose={handleTourClose}
        shouldStart={shouldStartTour}
        isReviewingTour={isReviewingTour}
        saveTourDismissed={saveTourDismissed}
        tourSession={tourSession}
      />
    </Styles.Container>
  );
};

export default VpsOciDetailPage;
